import { useState } from 'react';
import { type AppState, StorageService } from '@nextgen-web-framework/core';
import { useSelector } from 'react-redux';
import {
  AmwayUserDetails,
  ApiProjections,
  CartConstantsEnum,
  type CartEntryDetails,
  InitialCartState,
  cartApi,
  validateBundleInEntries,
} from '@commerce-cart-webcomponents/sdk';
import {
  Button,
  ButtonWidth,
  Checkbox,
  Drawer,
  FAIcon,
  Input,
  InputTypes,
  MLPVersion,
  NextgenImage,
  Theme,
  useTranslation,
} from '@nextgen-web-framework/all';
import { CartNotificationTheme } from '../../../../../common/components/cart-notification/types/enums';
import { TOAST_TIMEOUT } from '../../../../../common/types/enums';
import { useCartNotification } from '../../../../../common/hooks/useCartNotification';
import { ChangeOrderType } from '../../../../../common/components/change-order-type/ChangeOrderType';
import { disableProduct } from '../../../../../utils/commonUtils';
import styles from './MultipleItemDrawer.module.scss';
import { MultipleItemDrawerProps } from './types/propsType';
import { MultipleItemDrawerLoader } from './multiple-item-drawer-loader/MultipleItemDrawerLoader';

export const MultipleItemDrawer = ({
  listEntries,
  onSearchClick,
  onClose,
  imageURL,
  onConfirmSuccess,
  onOrderTypeChange,
}: MultipleItemDrawerProps) => {
  const t = useTranslation();
  const { createToast, deleteToast } = useCartNotification('multipleDrawer');
  const allEntries = listEntries?.map(({ product, bundleSlots, quantity, entryId }) => ({
    productId: product.id,
    bundleSlots,
    quantity,
    entryId,
  }));
  const { cart } = useSelector((state: AppState) => state.commerceCart) as InitialCartState;
  const [showLoader, setShowLoader] = useState(false);
  const [checkedCards, setCheckedCards] = useState(() =>
    cart.subCarts.map(({ amwayUser, subCartId }) => ({
      account: amwayUser as AmwayUserDetails,
      subCartId,
      checked: false,
    })),
  );
  const drawerHeaderTitle = t('cart.addToCartDrawer.drawerHeaderTitle');
  const addToCartText = t('cart.multipleItemDrawer.addNewCart');
  const activeCartText = t('cart.addToCartDrawer.activeCarts');
  const placeHolderText = t('cart.addToCartDrawer.placeHolderText');
  const confirmButtonText = t('cart.multipleItemDrawer.confirmButton');
  const sucessText = t('cart.multipleItemDrawer.confirmClickSuccess');
  const productSelectedCount = listEntries?.reduce((acc, listEntry) => acc + listEntry.quantity, 0);
  const productSelectedText = t('cart.multipleItemDrawer.productSelected', {
    count: String(productSelectedCount),
  });

  const handleCheckboxChange = (index: number) => {
    setCheckedCards((prevCheckedCards) =>
      prevCheckedCards.map((checkedCard, i) => {
        if (index === i) {
          return { ...checkedCard, checked: !checkedCard.checked };
        }
        return checkedCard;
      }),
    );
  };

  const handleConfirmClick = () => {
    const subCarts = checkedCards
      ?.filter(({ checked }) => checked)
      .map(({ subCartId }) => ({
        subCartId,
        entries: validateBundleInEntries(listEntries?.filter((entry) => !disableProduct(entry)) as CartEntryDetails[]),
      }));
    const payload = {
      subCarts,
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };
    const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);

    cartApi
      .addToCart({
        cartId,
        subCartId: cartId,
        payload,
        projections: ApiProjections.MIN,
      })
      .then(() => {
        onClose?.();
        onConfirmSuccess?.();
        createToast(
          {
            theme: CartNotificationTheme.SUCCESS,
            rightSeperator: false,
            content: sucessText,
            clickIcon: 'times',
            icon: 'check-circle',
            timeOut: TOAST_TIMEOUT,
          },
          deleteToast,
        );
      });
  };

  return (
    <>
      <Drawer
        header={drawerHeaderTitle}
        closeOnBackdropClick={true}
        footer={
          <Button
            disabled={!checkedCards.find(({ checked }) => checked === true)}
            onClick={handleConfirmClick}
            title={confirmButtonText}
            buttonWidth={ButtonWidth.FULL_WIDTH}
          />
        }
        closeIcon={true}
        bottomGap={0}
        onClose={onClose}
      >
        {showLoader ? (
          <MultipleItemDrawerLoader />
        ) : (
          <div className={styles['multiple-item-drawer-container']}>
            <ChangeOrderType
              onSuccessCB={onOrderTypeChange}
              onConfirmClickCB={() => setShowLoader(true)}
              cartData={cart}
              productDetails={allEntries}
            />
            <div className={styles['multiple-item-drawer-container-images']}>
              <div className={styles['multiple-item-drawer-container-images-frame']}>
                <div className={styles['item-images-container']}>
                  <NextgenImage src={imageURL || ''} alt={imageURL || ''} className={styles['item-images']} />
                </div>
                <div className={styles['item-selected-text']}>{productSelectedText}</div>
              </div>
            </div>

            <div className={styles['new-cart-text-container']}>
              <div className={styles['new-cart-text-container-title']}>{addToCartText}</div>
              <div className={styles['new-cart-text-container-input']}>
                <div className={styles['account-name-search']}>
                  <Input
                    disabled={false}
                    placeholder={placeHolderText}
                    type={InputTypes.TEXT}
                    mlpVersion={MLPVersion.TWO}
                    onClick={onSearchClick}
                    name="new-cart-name"
                  />
                  <FAIcon
                    iconName="search"
                    theme={Theme.BLACK}
                    className={styles['account-name-search-icon']}
                    onClick={onSearchClick}
                  />
                </div>
              </div>
            </div>

            <div className={styles['multiple-item-drawer-container-active-carts']}>
              <div className={styles['multiple-item-drawer-container-active-carts-title']}>{activeCartText}</div>
              <div className={styles['multiple-item-drawer-container-active-carts-list']}>
                {cart.subCarts.map((subCart, i) => (
                  <div key={subCart.subCartId} className={styles['check-box-account-card']}>
                    <div className={styles['check-box-account-card-account']}>
                      <div className={styles['check-box-account-card-account-name']}>
                        {subCart.amwayUser?.firstName} {subCart.amwayUser?.lastName}
                      </div>
                      <div className={styles['checkout-box-account-card-account-number']}>
                        {subCart.amwayUser?.accountId ?? ''}
                      </div>
                    </div>
                    <Checkbox onChange={() => handleCheckboxChange(i)} text="" checked={checkedCards[i].checked} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};
